


import React, { useEffect, useRef, useState } from "react";
import {
  Avatar, Box, Button, Chip, CircularProgress, Collapse, Divider,
  Drawer, IconButton, InputAdornment, LinearProgress, MenuItem,
  Pagination, Select, Skeleton, Tab, Tabs, TextField, Tooltip, Typography,
} from "@mui/material";
import { useSearchParams } from "react-router-dom";
import {
  Business as BusinessIcon,
  Work as WorkIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationOnIcon,
  Clear as ClearIcon,
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
  Star as StarIcon,
  History as HistoryIcon,
  Quiz as QuizIcon,
  Code as CodeIcon,
  TextSnippet as TextSnippetIcon,
  RecordVoiceOver as RecordVoiceOverIcon,
} from "@mui/icons-material";
import axios from "axios";
import { ThemeProvider } from "@mui/material/styles";
import recruiterTheme, {
  FONT_BODY, FONT_MONO, TYPE, RADIUS, COLORS, CONTROL_HEIGHT, ELLIPSIS, humanizeStatus,
} from "../../../styles/recruiterTheme";

const API = process.env.REACT_APP_API_BASE_URL;
const DETAIL_DRAWER_W = 680;
const SIDEBAR_W = 280;

const ALL_STATUSES = [
  "NewCandidate",
  "Recruiter_Rejected","Recruiter_Accepted","Recruiter_Hold",
  "HiringManager_Rejected","HiringManager_Accepted","HiringManager_Hold",
  "ScreeningTest_Sent","ScreeningTest_Resent",
  "ScreeningTest_Passed","ReScreeningTest_Passed",
  "ScreeningTest_Failed","ReScreeningTest_Failed",
  "OnHold_TestPassed","Interview_Scheduled",
  "Round1_Rejected","Round2_Suggested","Round2_Scheduled",
  "Interviewer_Selected","Interviewer_Rejected",
  "Selected","Rejected",
  "Candidate_Declined","Candidate_OnHold","Candidate_Quit",
  "OnHold_ReScreening","OnHold_Screening",
  "TestPassed_Rejected","ReTestPassed_Rejected",
];

const STATUS_COLOR = (s) => {
  if (!s) return "default";
  if (s.includes("Selected") || s === "Selected") return "success";
  if (s.includes("Rejected") || s.includes("Failed"))  return "error";
  if (s.includes("Hold") || s.includes("Suggested"))   return "warning";
  if (s.includes("Sent") || s.includes("Scheduled"))   return "info";
  if (s === "NewCandidate") return "default";
  return "primary";
};

const authHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});
const initials = (name) =>
  (name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("");
const fmt = (v, suffix = "") => (v != null && v !== "" ? `${v}${suffix}` : "—");
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" }) : "—";
const fmtDateTime = (d) => d ? new Date(d).toLocaleString("en-IN", { day:"2-digit", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" }) : "—";

// ── Reusable pieces ───────────────────────────────────────────────────────────

function LabelVal({ label, value, mono = false }) {
  return (
    <Box>
      <Typography fontSize={TYPE.overline} fontWeight={700} color="#94a3b8" textTransform="uppercase" letterSpacing="0.06em">
        {label}
      </Typography>
      <Typography fontSize={TYPE.body} fontWeight={500} color="#1e293b" fontFamily={mono ? FONT_MONO : undefined}>
        {value ?? "—"}
      </Typography>
    </Box>
  );
}

function SectionHead({ icon: Icon, label }) {
  return (
    <Box display="flex" alignItems="center" gap={1} mb={1.5} mt={2.5}>
      <Icon sx={{ fontSize: 16, color: "#8B7DB5" }} />
      <Typography fontSize={TYPE.caption} fontWeight={700} color="#8B7DB5" textTransform="uppercase" letterSpacing="0.06em">
        {label}
      </Typography>
      <Box flex={1} height="1px" bgcolor="#e2e8f0" ml={1} />
    </Box>
  );
}

function ScoreBar({ label, score, max = 5 }) {
  const pct = Math.min(100, ((score || 0) / max) * 100);
  return (
    <Box mb={1}>
      <Box display="flex" justifyContent="space-between" mb={0.3}>
        <Typography fontSize={TYPE.caption} color="#64748b">{label}</Typography>
        <Typography fontSize={TYPE.caption} fontWeight={700} color="#0f172a">{score}/{max}</Typography>
      </Box>
      <LinearProgress variant="determinate" value={pct} sx={{
        height: 6, borderRadius: 3, bgcolor: "#e2e8f0",
        "& .MuiLinearProgress-bar": {
          bgcolor: pct >= 80 ? "#22c55e" : pct >= 50 ? "#f59e0b" : "#ef4444",
          borderRadius: 3,
        },
      }} />
    </Box>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  CANDIDATE DETAIL DRAWER
// ══════════════════════════════════════════════════════════════════════════════

function CandidateDetailDrawer({ candidateId, open, onClose }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");
  const [tab, setTab]         = useState(0);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!open || !candidateId) return;
    let cancelled = false;
    setData(null); setTab(0); setError(""); setLoading(true);
    axios
      .get(`${API}/candidate-browser/candidate/${candidateId}`, authHeader())
      .then((r) => {
        if (cancelled) return;
        if (r.data?.data) setData(r.data.data);
        else setError("Candidate details are not available.");
      })
      .catch((err) => {
        console.error(err);
        if (!cancelled) setError(err.response?.data?.message || "Could not load candidate details.");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, candidateId, reloadKey]);

  const c = data;
const handleViewResume = async (resumeUrl, candidateID) => {
  try {
    const url = resumeUrl.startsWith("http") ? resumeUrl : `${API}/${resumeUrl}`;

    const response = await axios.get(url, {
      ...authHeader(),
      responseType: "blob", 
    });

    const blob = new Blob([response.data], { type: response.data.type || "application/pdf" });
    const blobUrl = window.URL.createObjectURL(blob);
    window.open(blobUrl, "_blank");

    // optional cleanup after a delay
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60_000);
  } catch (err) {
    console.error("Failed to load resume:", err);
    alert("Could not load resume. Please try again.");
  }
};
  /* ── TAB 0: Profile ── */
  const tabProfile = () => (
    <Box px={3} pb={4}>
      <SectionHead icon={PersonIcon} label="Personal Information" />
      <Box display="grid" gridTemplateColumns="1fr 1fr 1fr" gap={2.5}>
        <LabelVal label="Full Name"           value={c.candidatename} />
        <LabelVal label="Candidate ID"        value={c.candidateID} mono />
        <LabelVal label="Email"               value={c.candidateEmail} />
        <LabelVal label="Phone"               value={c.phone} />
        <LabelVal label="Address"             value={c.address} />
        <LabelVal label="Present Location"    value={c.presentLocation} />
        <LabelVal label="Preferred Location"  value={c.preferredLocation} />
        <LabelVal label="Willing to Relocate" value={c.willingToRelocate} />
      </Box>

      <SectionHead icon={WorkIcon} label="Professional Details" />
      <Box display="grid" gridTemplateColumns="1fr 1fr 1fr" gap={2.5}>
        <LabelVal label="Job Role"         value={c.jobRole?.replace(/_/g," ")} />
        <LabelVal label="Total Experience" value={fmt(c.totalExperience," yrs")} />
        <LabelVal label="Current CTC"      value={fmt(c.currentCTC," LPA")} />
        <LabelVal label="Expected CTC"     value={fmt(c.expectedCTC," LPA")} />
        <LabelVal label="Notice Period"    value={fmt(c.noticePeriod," days")} />
        <LabelVal label="Match Score"      value={fmt(c.match_score,"%")} />
        <LabelVal label="Uploaded"         value={fmtDate(c.uploadedAt)} />
        <LabelVal label="JD Reference"     value={c.jdID} mono />
      </Box>


    {c.resumeUrl && (
      <>
        <SectionHead icon={TextSnippetIcon} label="Resume" />
        <Box
          onClick={() => handleViewResume(c.resumeUrl, c.candidateID)}
          display="inline-flex" alignItems="center" gap={0.8}
          sx={{
            fontSize:TYPE.small, color:"#8B7DB5", fontWeight:600, cursor:"pointer",
            border:"1px solid #6750A4", borderRadius:`${RADIUS.sm}px`, px:1.5, py:0.8,
            "&:hover": { bgcolor:"#EDE7F6" },
          }}>
          <TextSnippetIcon sx={{ fontSize:16 }} />
          View Resume
          <OpenInNewIcon sx={{ fontSize:13 }} />
        </Box>
      </>
    )}
      {c.summaries && (
        <>
          <SectionHead icon={TextSnippetIcon} label="AI Summary" />
          <Typography fontSize={TYPE.body} color="#334155" lineHeight={1.7}
            sx={{ bgcolor:"#f8fafc", border:"1px solid #e2e8f0", borderRadius:`${RADIUS.sm}px`, p:1.5 }}>
            {c.summaries}
          </Typography>
        </>
      )}

      {c.remarks && (
        <>
          <SectionHead icon={TextSnippetIcon} label="Remarks" />
          <Typography fontSize={TYPE.body} color="#334155">{c.remarks}</Typography>
        </>
      )}
    </Box>
  );

  /* ── TAB 1: Feedback ── */
  const tabFeedback = () => (
    <Box px={3} pb={4}>
      <SectionHead icon={RecordVoiceOverIcon} label="Current Status" />
      <Box display="flex" alignItems="center" gap={1.5} mb={2}>
        <Chip
          label={humanizeStatus(c.overallStatus)}
          color={STATUS_COLOR(c.overallStatus)}
          sx={{ fontWeight:700, fontSize:TYPE.small, px:1 }}
        />
        <Typography fontSize={TYPE.caption} color="#64748b">Token: {c.tokenStatus}</Typography>
      </Box>

      {[
        ["Recruiter Feedback",      c.recruiterFeedback],
        ["Hiring Manager Feedback", c.hiringManagerFeedback],
        ["Test Feedback",           c.testFeedback],
      ].map(([label, val]) => val ? (
        <Box key={label} mb={2}>
          <Typography fontSize={TYPE.overline} fontWeight={700} color="#94a3b8" textTransform="uppercase" letterSpacing="0.06em" mb={0.5}>
            {label}
          </Typography>
          <Typography fontSize={TYPE.body} color="#334155"
            sx={{ bgcolor:"#f8fafc", border:"1px solid #e2e8f0", borderRadius:`${RADIUS.sm}px`, p:1.5 }}>
            {val}
          </Typography>
        </Box>
      ) : null)}

      {c.interviewFeedback?.length > 0 && (
        <>
          <SectionHead icon={RecordVoiceOverIcon} label="Interview Feedback" />
          {c.interviewFeedback.map((f, i) => (
            <Box key={i} mb={2} p={2}
              sx={{ border:"1px solid #e2e8f0", borderRadius:`${RADIUS.sm}px`, bgcolor:"#fff" }}>
              <Box display="flex" justifyContent="space-between" mb={1.5}>
                <Typography fontSize={TYPE.small} fontWeight={700} color="#0f172a">Round {i + 1}</Typography>
                {f.reviewRating && (
                  <Chip label={humanizeStatus(f.reviewRating)} size="small"
                    color={STATUS_COLOR(f.reviewRating)} sx={{ fontSize:TYPE.caption }} />
                )}
              </Box>
              <ScoreBar label="Programming"      score={f.programmingRating}    max={5} />
              <ScoreBar label="Technical Skills" score={f.technicalSkills}      max={5} />
              <ScoreBar label="Communication"    score={f.communicationSkills}  max={5} />
              <ScoreBar label="Problem Solving"  score={f.problemSolvingSkills} max={5} />
              {f.feedbackText && (
                <Typography fontSize={TYPE.small} color="#64748b" mt={1}
                  sx={{ borderTop:"1px solid #f1f5f9", pt:1 }}>
                  {f.feedbackText}
                </Typography>
              )}
            </Box>
          ))}
        </>
      )}
    </Box>
  );

  /* ── TAB 2: Screening ── */
  const tabScreening = () => {
    const mcqs  = c.mcq_questions         || [];
    const subjs = c.subjective_questions  || [];
    const codes = c.programming_questions || [];
    const noData = !mcqs.length && !subjs.length && !codes.length;

    if (noData) return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height={280} gap={1} color={COLORS.faint}>
        <QuizIcon sx={{ fontSize:48, mb:1 }} />
        <Typography fontSize={TYPE.body}>No screening test data</Typography>
      </Box>
    );

    return (
      <Box px={3} pb={4}>
        {c.ScreeningTestScore && (
          <Box display="flex" alignItems="center" gap={1.5} my={2}
            sx={{ bgcolor:"#EDE7F6", border:"1px solid #6750A4", borderRadius:`${RADIUS.sm}px`, p:1.5 }}>
            <StarIcon sx={{ color:"#f59e0b" }} />
            <Typography fontSize={TYPE.body} fontWeight={700} color="#6750A4">
              Overall Score: {c.ScreeningTestScore}%
            </Typography>
          </Box>
        )}

        {mcqs.length > 0 && (
          <>
            <SectionHead icon={QuizIcon} label={`MCQ (${mcqs.length} questions)`} />
            {mcqs.map((q, i) => {
              const correct =
                JSON.stringify([...(q.correct_answer||[])].sort()) ===
                JSON.stringify([...(q.user_answer||[])].sort());
              return (
                <Box key={i} mb={1.5} p={1.5}
                  sx={{
                    border:"1px solid",
                    borderColor: correct ? "#bbf7d0" : "#fecaca",
                    borderRadius:`${RADIUS.sm}px`,
                    bgcolor: correct ? "#f0fdf4" : "#fff1f2",
                  }}>
                  <Typography fontSize={TYPE.small} fontWeight={600} color="#0f172a" mb={1}>
                    Q{i+1}. {q.question}
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={0.8}>
                    {q.options?.map((opt, oi) => {
                      const isCorrect = q.correct_answer?.includes(opt);
                      const isUser    = q.user_answer?.includes(opt);
                      return (
                        <Chip key={oi} label={opt} size="small" sx={{
                          fontSize:TYPE.caption,
                          bgcolor: isCorrect ? "#dcfce7" : isUser && !isCorrect ? "#fee2e2" : "#f1f5f9",
                          color:   isCorrect ? "#15803d" : isUser && !isCorrect ? "#b91c1c" : "#64748b",
                          border:"1px solid",
                          borderColor: isCorrect ? "#86efac" : isUser && !isCorrect ? "#fca5a5" : "#e2e8f0",
                          fontWeight: (isCorrect || isUser) ? 700 : 400,
                        }} />
                      );
                    })}
                  </Box>
                </Box>
              );
            })}
          </>
        )}

        {subjs.length > 0 && (
          <>
            <SectionHead icon={TextSnippetIcon} label={`Subjective (${subjs.length} questions)`} />
            {subjs.map((q, i) => (
              <Box key={i} mb={2} p={1.5} sx={{ border:"1px solid #e2e8f0", borderRadius:`${RADIUS.sm}px`, bgcolor:"#fff" }}>
                <Box display="flex" justifyContent="space-between" mb={0.8}>
                  <Typography fontSize={TYPE.small} fontWeight={600} color="#0f172a">
                    Q{i+1}. {q.question}
                  </Typography>
                  <Chip
                    label={`${q.score ?? 0}/${q.max_score ?? 10}`} size="small"
                    color={(q.score||0) >= (q.max_score||10)*0.7 ? "success" : (q.score||0) >= (q.max_score||10)*0.4 ? "warning" : "error"}
                    sx={{ fontSize:TYPE.caption, height:24, fontWeight:700, flexShrink:0, ml:1 }}
                  />
                </Box>
                {q.skill && <Chip label={q.skill} size="small" variant="outlined" sx={{ fontSize:TYPE.caption, height:24, mb:1, mr:0.8 }} />}
                {q.difficulty && <Chip label={q.difficulty} size="small" variant="outlined" sx={{ fontSize:TYPE.caption, height:24, mb:1 }} />}
                {q.user_answer ? (
                  <Box mt={1}>
                    <Typography fontSize={TYPE.overline} fontWeight={700} color="#94a3b8" textTransform="uppercase" letterSpacing="0.06em" mb={0.3}>
                      Candidate Answer
                    </Typography>
                    <Typography fontSize={TYPE.small} color="#334155" lineHeight={1.6}
                      sx={{ bgcolor:"#f8fafc", border:"1px solid #f1f5f9", borderRadius:`${RADIUS.sm}px`, p:1 }}>
                      {q.user_answer}
                    </Typography>
                  </Box>
                ) : (
                  <Typography fontSize={TYPE.caption} color="#94a3b8" mt={1}>No answer submitted</Typography>
                )}
              </Box>
            ))}
          </>
        )}

        {codes.length > 0 && (
          <>
            <SectionHead icon={CodeIcon} label={`Coding (${codes.length} questions)`} />
            {codes.map((q, i) => (
              <Box key={i} mb={2} p={1.5} sx={{ border:"1px solid #e2e8f0", borderRadius:`${RADIUS.sm}px`, bgcolor:"#fff" }}>
                <Box display="flex" justifyContent="space-between" mb={0.8}>
                  <Typography fontSize={TYPE.small} fontWeight={600} color="#0f172a">
                    Q{i+1}. {q.question}
                  </Typography>
                  <Box display="flex" gap={0.8}>
                    <Chip label={q.language} size="small" color="info" sx={{ fontSize:TYPE.caption }} />
                    <Chip label={`Score: ${q.score ?? 0}`} size="small"
                      color={q.score > 0 ? "success" : "default"} sx={{ fontSize:TYPE.caption, height:24 }} />
                  </Box>
                </Box>
                {q.user_code && (
                  <Box component="pre" sx={{
                    fontSize:TYPE.caption, bgcolor:"#0f172a", color:"#e2e8f0",
                    borderRadius:`${RADIUS.sm}px`, p:1.5, overflow:"auto", maxHeight:240, mt:1,
                    fontFamily:FONT_MONO, whiteSpace:"pre-wrap", wordBreak:"break-all",
                  }}>
                    {q.user_code}
                  </Box>
                )}
              </Box>
            ))}
          </>
        )}
      </Box>
    );
  };

  /* ── TAB 3: History ── */
  const tabHistory = () => {
    const history = c.statusHistory || [];
    if (!history.length) return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height={280} gap={1} color={COLORS.faint}>
        <HistoryIcon sx={{ fontSize:48, mb:1 }} />
        <Typography fontSize={TYPE.body}>No status history recorded</Typography>
      </Box>
    );
    const dotColor = (s) => {
      const c = STATUS_COLOR(s);
      return c==="success" ? "#22c55e" : c==="error" ? "#ef4444" : c==="warning" ? "#f59e0b" : c==="info" ? "#3b82f6" : "#94a3b8";
    };
    return (
      <Box px={3} pb={4} pt={1}>
        <Box position="relative" pl={3}>
          <Box position="absolute" left={8} top={8} bottom={8} width="2px" bgcolor="#e2e8f0" />
          {[...history].reverse().map((h, i) => (
            <Box key={i} position="relative" mb={2.5}>
              <Box position="absolute" left={-19} top={4} width={10} height={10} borderRadius="50%"
                bgcolor={dotColor(h.status)} border="2px solid #fff"
                sx={{ boxShadow:"0 0 0 2px #e2e8f0" }}
              />
              <Chip label={humanizeStatus(h.status)} size="small"
                color={STATUS_COLOR(h.status)} sx={{ fontWeight:700, fontSize:TYPE.caption, mb:0.5 }} />
              <Typography fontSize={TYPE.caption} color="#64748b">
                {fmtDateTime(h.timestamp)}
                {h.changedBy && h.changedBy !== "system" && ` · by ${h.changedBy}`}
                {h.role && h.role !== "system" && ` (${h.role})`}
              </Typography>
              {h.note && (
                <Typography fontSize={TYPE.small} color="#334155" mt={0.4}
                  sx={{ bgcolor:"#f8fafc", border:"1px solid #e2e8f0", borderRadius:`${RADIUS.sm}px`, px:1, py:0.5 }}>
                  {h.note}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  const TABS = [
    { label:"Profile",   icon:<PersonIcon sx={{ fontSize:15 }} /> },
    { label:"Feedback",  icon:<RecordVoiceOverIcon sx={{ fontSize:15 }} /> },
    { label:"Screening", icon:<QuizIcon sx={{ fontSize:15 }} /> },
    { label:"History",   icon:<HistoryIcon sx={{ fontSize:15 }} /> },
  ];

  return (
    <Drawer anchor="right" open={open} onClose={onClose}
      PaperProps={{ sx: { width:DETAIL_DRAWER_W, boxShadow:"-4px 0 24px rgba(0,0,0,0.1)" } }}>
      {/* header */}
      <Box sx={{
        px:3, py:2, borderBottom:"1px solid #e2e8f0", bgcolor:"#fff",
        display:"flex", alignItems:"center", gap:2,
        position:"sticky", top:0, zIndex:10,
      }}>
        {!loading && error ? (
          <Typography sx={{ flex:1, fontFamily:FONT_BODY, fontSize:TYPE.cardTitle, fontWeight:700, color:"#0f172a" }}>Candidate</Typography>
        ) : loading || !c ? (
          <>
            <Skeleton variant="circular" width={48} height={48} />
            <Box flex={1}><Skeleton width={160} height={20} /><Skeleton width={120} height={14} /></Box>
          </>
        ) : (
          <>
            <Avatar sx={{ width:48, height:48, bgcolor:"#8B7DB5", fontSize:TYPE.cardTitle, fontWeight:700, flexShrink:0 }}>
              {initials(c.candidatename)}
            </Avatar>
            <Box flex={1} minWidth={0}>
              <Typography fontSize={TYPE.cardTitle} fontWeight={700} color="#0f172a" noWrap>
                {c.candidatename}
              </Typography>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Typography fontSize={TYPE.caption} color="#94a3b8">{c.candidateID}</Typography>
                <Chip label={humanizeStatus(c.overallStatus)} size="small"
                  color={STATUS_COLOR(c.overallStatus)} sx={{ fontSize:TYPE.caption, height:24 }} />
              </Box>
            </Box>
          </>
        )}
        <IconButton size="small" onClick={onClose} sx={{ flexShrink:0 }}>
          <CloseIcon sx={{ fontSize:18 }} />
        </IconButton>
      </Box>

      {loading && <LinearProgress />}

      {!loading && error && (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap={1.5} py={6} px={3} textAlign="center">
          <Typography sx={{ fontFamily:FONT_BODY, fontSize:TYPE.body, color:COLORS.error }}>{error}</Typography>
          <Button variant="outlined" size="small" onClick={() => setReloadKey((k) => k + 1)}
            sx={{ height:CONTROL_HEIGHT.sm, textTransform:"none", fontWeight:600 }}>
            Retry
          </Button>
        </Box>
      )}

      {!loading && c && (
        <>
          <Tabs value={tab} onChange={(_,v) => setTab(v)} variant="fullWidth"
            sx={{
              borderBottom:"1px solid #e2e8f0", minHeight:40,
              "& .MuiTab-root": { minHeight:40, fontSize:TYPE.small, textTransform:"none", fontWeight:600 },
              position:"sticky", top:73, bgcolor:"#fff", zIndex:9,
            }}>
            {TABS.map((t,i) => <Tab key={i} label={t.label} icon={t.icon} iconPosition="start" />)}
          </Tabs>
          <Box sx={{ overflowY:"auto", flex:1 }}>
            {tab === 0 && tabProfile()}
            {tab === 1 && tabFeedback()}
            {tab === 2 && tabScreening()}
            {tab === 3 && tabHistory()}
          </Box>
        </>
      )}
    </Drawer>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  PANE CHROME (shared header strip + empty state, identical in all three panes)
// ══════════════════════════════════════════════════════════════════════════════

const PANE_HEADER_H = 48;
const OVERLINE_SX = {
  fontFamily: FONT_BODY, fontSize: TYPE.overline, fontWeight: 700, color: "#64748b",
  textTransform: "uppercase", letterSpacing: "0.06em", lineHeight: 1.2,
};
const META_LABEL_SX = { fontFamily: FONT_BODY, fontSize: TYPE.overline, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", whiteSpace: "nowrap" };
const META_VALUE_SX = { fontFamily: FONT_BODY, fontSize: TYPE.small, fontWeight: 600, color: "#334155", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" };
const PILL_SX = { height: 24, fontFamily: FONT_BODY, fontWeight: 600, "& .MuiChip-label": { fontSize: TYPE.caption, px: 1 } };
const fmtShortDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? String(d) : dt.toLocaleDateString("en-GB", { day:"2-digit", month:"short", year:"numeric" });
};

function PaneHeader({ label, title, bgcolor = "inherit" }) {
  return (
    <Box sx={{
      height: PANE_HEADER_H, flexShrink: 0, display: "flex", alignItems: "center",
      px: 2, borderBottom: "1px solid #e2e8f0", bgcolor, minWidth: 0,
    }}>
      <Typography component="div" title={title} sx={{ ...OVERLINE_SX, ...ELLIPSIS }}>{label}</Typography>
    </Box>
  );
}

function PaneEmpty({ icon: Icon, text, sx = {} }) {
  return (
    <Box sx={{
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      gap: 1, py: 5, px: 2, height: "100%", minHeight: 200, textAlign: "center", ...sx,
    }}>
      <Icon sx={{ fontSize: 40, color: COLORS.faint }} />
      <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.body, color: COLORS.muted }}>{text}</Typography>
    </Box>
  );
}

function PaneError({ text, onRetry, sx = {} }) {
  return (
    <Box sx={{
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      gap: 1.5, py: 5, px: 2, height: "100%", minHeight: 200, textAlign: "center", ...sx,
    }}>
      <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.body, color: COLORS.error }}>{text}</Typography>
      {onRetry && (
        <Button variant="outlined" size="small" onClick={onRetry}
          sx={{ height: CONTROL_HEIGHT.sm, textTransform: "none", fontWeight: 600 }}>
          Retry
        </Button>
      )}
    </Box>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  CANDIDATE CARD
// ══════════════════════════════════════════════════════════════════════════════

function CandidateCard({ c, onClick }) {
  return (
    <Box onClick={onClick} sx={{
      display:"flex", alignItems:"flex-start", gap:2,
      p:2, border:"1px solid #e2e8f0", borderRadius:`${RADIUS.md}px`, bgcolor:"#fff",
      cursor:"pointer", minWidth:0,
      transition:"box-shadow 0.15s, border-color 0.15s",
      "&:hover": { boxShadow:"0 2px 12px rgba(0,0,0,0.09)", borderColor:"#6750A4" },
    }}>
      <Avatar sx={{ width:40, height:40, bgcolor:"#8B7DB5", fontSize:TYPE.body, fontWeight:700, flexShrink:0 }}>
        {initials(c.candidatename)}
      </Avatar>
      <Box flex={1} minWidth={0}>
        <Box display="flex" alignItems="center" gap={1} minWidth={0}>
          <Typography title={c.candidatename} sx={{ fontFamily:FONT_BODY, fontSize:TYPE.body, fontWeight:700, color:"#0f172a", ...ELLIPSIS, flexShrink:1 }}>{c.candidatename}</Typography>
          <Typography sx={{ fontFamily:FONT_MONO, fontSize:TYPE.caption, color:"#94a3b8", whiteSpace:"nowrap", flexShrink:0 }}>{c.candidateID}</Typography>
          <Chip label={humanizeStatus(c.overallStatus)} size="small"
            color={STATUS_COLOR(c.overallStatus)} sx={{ ...PILL_SX, ml:"auto", flexShrink:0 }} />
        </Box>
        <Box display="flex" columnGap={2.5} rowGap={0.5} mt={0.5} flexWrap="wrap" minWidth={0}>
          {[[EmailIcon,c.candidateEmail],[PhoneIcon,c.phone],[LocationOnIcon,c.presentLocation||"—"]].map(([Icon,val],i) => (
            <Box key={i} display="flex" alignItems="center" gap={0.5} minWidth={0} maxWidth="100%">
              <Icon sx={{ fontSize:14, color:"#94a3b8", flexShrink:0 }} />
              <Typography title={val || ""} sx={{ fontFamily:FONT_BODY, fontSize:TYPE.caption, color:"#64748b", ...ELLIPSIS }}>{val}</Typography>
            </Box>
          ))}
        </Box>
        <Box display="flex" columnGap={3} rowGap={1} mt={1.5} flexWrap="wrap">
          {[
            ["Experience", c.totalExperience != null ? `${c.totalExperience} yr` : "—"],
            ["Current CTC", c.currentCTC != null ? `₹${c.currentCTC} LPA` : "—"],
            ["Expected CTC", c.expectedCTC != null ? `₹${c.expectedCTC} LPA` : "—"],
            ["Notice", c.noticePeriod != null ? `${c.noticePeriod} days` : "—"],
            ["Score", c.match_score != null ? `${c.match_score}%` : c.ScreeningTestScore ? `${c.ScreeningTestScore}%` : "—"],
          ].map(([label,val]) => (
            <Box key={label}>
              <Typography sx={META_LABEL_SX}>{label}</Typography>
              <Typography sx={META_VALUE_SX}>{val}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  COMPANY SIDEBAR / JD ACCORDION / STATUS BAR  (unchanged from v1)
// ══════════════════════════════════════════════════════════════════════════════

function CompanySidebar({ companies, loading, error, onRetry, selected, onSelect }) {
  return (
    <Box sx={{ width:SIDEBAR_W, flexShrink:0, borderRight:"1px solid #e2e8f0", height:"100%", display:"flex", flexDirection:"column", bgcolor:"#f8fafc", minWidth:0 }}>
      <PaneHeader label="Companies" />
      <Box sx={{ flex:1, overflowY:"auto", py:1 }}>
      {loading
        ? Array.from({length:5}).map((_,i) => <Skeleton key={i} height={56} sx={{ mx:1, borderRadius:`${RADIUS.sm}px`, mb:0.5 }} />)
        : error
        ? <PaneError text={error} onRetry={onRetry} />
        : companies.length === 0
        ? <PaneEmpty icon={BusinessIcon} text="No companies found" />
        : companies.map((c, idx) => {
          const companyLabel = c.companyName || "Unknown Company";
          const active = selected?.companyName === c.companyName;
          return (
            <Box key={c.companyName || `unknown-${idx}`} onClick={() => onSelect(c)} sx={{
              display:"flex", alignItems:"center", gap:1.5, px:1.5, py:1, mx:1, my:0.5, minHeight:56,
              borderRadius:`${RADIUS.sm}px`, cursor:"pointer",
              bgcolor: active ? "#EDE7F6" : "transparent",
              borderLeft: active ? "3px solid #8B7DB5" : "3px solid transparent",
              transition:"all 0.15s", "&:hover": { bgcolor: active ? "#EDE7F6" : "#f1f5f9" },
            }}>
              <Avatar sx={{ width:32, height:32, fontSize:TYPE.small, fontWeight:700, bgcolor: active ? "#8B7DB5" : "#94a3b8", flexShrink:0 }}>
                {companyLabel[0]}
              </Avatar>
              <Box flex={1} minWidth={0}>
                <Typography title={companyLabel} sx={{ fontFamily:FONT_BODY, fontSize:TYPE.body, lineHeight:1.4, fontWeight:active?700:600, color:active?"#6750A4":"#1e293b", ...ELLIPSIS }}>
                  {companyLabel}
                </Typography>
                <Typography sx={{ fontFamily:FONT_BODY, fontSize:TYPE.caption, lineHeight:1.4, color:"#94a3b8", fontVariantNumeric:"tabular-nums", ...ELLIPSIS }}>
                  {c.jdCount} JD{c.jdCount!==1?"s":""} · {c.candidateCount} candidates
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function JdAccordion({ jd, isOpen, onToggle, selectedJdId, onSelectJd }) {
  const active = selectedJdId === jd._id;
  const role = jd.jobRole?.replace(/_/g," ");
  const meta = `${jd.jdID} · ${jd.experience} yrs · ${jd.programmingLanguage}`;
  return (
    <Box sx={{ border:"1px solid", borderColor:active?"#6750A4":"#e2e8f0", borderRadius:`${RADIUS.sm}px`, mb:1, overflow:"hidden", bgcolor:active?"#EDE7F6":"#fff", transition:"all 0.15s" }}>
      <Box 
    onClick={() => {
  onToggle(jd._id);
  if (selectedJdId !== jd._id) {  
    onSelectJd(jd);
  }

       }}
        sx={{ display:"flex", alignItems:"center", gap:1, px:1.5, py:1.25, minHeight:56, cursor:"pointer", "&:hover":{ bgcolor: active ? "#E8DEF8" : "#f8fafc" } }}>
        <WorkIcon sx={{ fontSize:18, color:active?"#8B7DB5":"#94a3b8", flexShrink:0, mr:0.5 }} />
        <Box flex={1} minWidth={0}>
          <Typography title={role} sx={{ fontFamily:FONT_BODY, fontSize:TYPE.body, lineHeight:1.4, fontWeight:600, color:active?"#6750A4":"#1e293b", ...ELLIPSIS }}>{role}</Typography>
          <Typography title={meta} sx={{ fontFamily:FONT_BODY, fontSize:TYPE.caption, lineHeight:1.4, color:"#64748b", ...ELLIPSIS }}>{meta}</Typography>
        </Box>
        <Chip label={`${jd.candidateCount}`} size="small" sx={{ ...PILL_SX, flexShrink:0, fontVariantNumeric:"tabular-nums" }} color={active?"primary":"default"} />
        <Chip label={jd.is_active?"Active":"Closed"} size="small" color={jd.is_active?"success":"default"} sx={{ ...PILL_SX, flexShrink:0 }} />
        {isOpen ? <ExpandLessIcon sx={{ fontSize:18, flexShrink:0 }} /> : <ExpandMoreIcon sx={{ fontSize:18, flexShrink:0 }} />}
      </Box>
      <Collapse in={isOpen}>
        <Divider />
        <Box px={1.5} py={1.25} display="flex" columnGap={3} rowGap={1} flexWrap="wrap">
          {[["Salary",jd.salaryRange],["Positions",jd.openPositions],["Expiry",jd.expiration_time?fmtShortDate(jd.expiration_time):"—"]].map(([l,v]) => (
            <Box key={l}>
              <Typography sx={META_LABEL_SX}>{l}</Typography>
              <Typography sx={META_VALUE_SX}>{v??"—"}</Typography>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}

function StatusBar({ summary }) {
  const total = Object.values(summary).reduce((a,b) => a+b, 0);
  if (!total) return null;
  return (
    <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
      {Object.entries(summary).sort((a,b)=>b[1]-a[1]).map(([status,count]) => (
        <Chip key={status} label={`${humanizeStatus(status)} · ${count}`}
          size="small" color={STATUS_COLOR(status)} variant="outlined" sx={{ ...PILL_SX, fontVariantNumeric:"tabular-nums" }} />
      ))}
    </Box>
  );
}

//  MAIN PAGE

// `height`: the host dashboard passes 100vh minus its own top bar, so the page never scrolls.
export default function CandidateBrowserPage({ initialCompany, height = "calc(100vh - 64px)" } = {}) {
  const [searchParams] = useSearchParams();
  const [companies, setCompanies]          = useState([]);
  const [companiesLoading, setCompLoading] = useState(true);
  const [selectedCompany, setSelectedComp] = useState(null);

  const [jds, setJds]               = useState([]);
  const [jdsLoading, setJdsLoading] = useState(false);
  const [openJdId, setOpenJdId]     = useState(null);
  const [selectedJd, setSelectedJd] = useState(null);

  const [candidates, setCandidates]     = useState([]);
  const [candLoading, setCandLoading]   = useState(false);
  const [total, setTotal]               = useState(0);
  const [pages, setPages]               = useState(1);
  const [statusSummary, setStatusSummary] = useState({});

  const [search, setSearch]              = useState("");
  const [statusFilter, setStatusFilter]  = useState("");
  const [page, setPage]                  = useState(1);
  const LIMIT = 15;

  // detail drawer
  const [drawerOpen, setDrawerOpen]         = useState(false);
  const [selectedCandId, setSelectedCandId] = useState(null);

  const [searchInput, setSearchInput]    = useState("");
  const [companiesError, setCompaniesError] = useState("");
  const [jdsError, setJdsError]             = useState("");
  const [candError, setCandError]           = useState("");
  const [companiesReload, setCompaniesReload] = useState(0);
  const [jdsReload, setJdsReload]             = useState(0);
  const [candReload, setCandReload]           = useState(0);

  const searchTimer = useRef(null);
  useEffect(() => () => clearTimeout(searchTimer.current), []);

const effectiveCompanyFilter = initialCompany || searchParams.get("company");

const displayedCompanies = effectiveCompanyFilter
  ? companies.filter((c) => c.companyName === decodeURIComponent(effectiveCompanyFilter))
  : companies;
  useEffect(() => {
    let cancelled = false;
    setCompLoading(true); setCompaniesError("");
    axios.get(`${API}/candidate-browser/companies`, authHeader())
      .then((r) => { if (!cancelled) setCompanies(r.data?.data || []); })
      .catch((err) => {
        console.error(err);
        if (!cancelled) setCompaniesError(err.response?.data?.message || "Could not load companies.");
      })
      .finally(() => { if (!cancelled) setCompLoading(false); });
    return () => { cancelled = true; };
  }, [companiesReload]);


useEffect(() => {
  if (!selectedCompany) return;
  // Ignore a slower response for a company the user has already moved away from.
  let cancelled = false;
  setJdsLoading(true); setJdsError(""); setJds([]); setSelectedJd(null); setCandidates([]); setStatusSummary({});
  axios.get(`${API}/candidate-browser/jds`, { ...authHeader(), params:{ company:selectedCompany.companyName } })
    .then((r) => { if (!cancelled) setJds(r.data?.data || []); })
    .catch((err) => {
      console.error(err);
      if (!cancelled) setJdsError(err.response?.data?.message || "Could not load job descriptions.");
    })
    .finally(() => { if (!cancelled) setJdsLoading(false); });
  return () => { cancelled = true; };
}, [selectedCompany, jdsReload]);


useEffect(() => {
  if (!companies.length) return;
  const companyParam = initialCompany || searchParams.get("company");
  if (!companyParam) return;
  const decoded = decodeURIComponent(companyParam);
  const match = companies.find((c) => c.companyName === decoded);
  if (match && selectedCompany?.companyName !== decoded) {
    setSelectedComp(match);
  }
}, [companies, searchParams, initialCompany]); // eslint-disable-line react-hooks/exhaustive-deps

useEffect(() => {
  if (!jds.length) return;
  if (selectedJd) return;
  if (!(initialCompany || searchParams.get("company"))) return;
  const firstJd = jds[0];
  setSelectedJd(firstJd);
  setOpenJdId(firstJd._id);
}, [jds, initialCompany, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Candidates for the selected JD / page / filters; a stale response (JD or
  // filter changed meanwhile) is dropped.
  useEffect(() => {
    if (!selectedJd) return;
    let cancelled = false;
    setCandLoading(true); setCandError("");
    const params = { jddetails_id:selectedJd._id, page, limit:LIMIT };
    if (statusFilter) params.overallStatus = statusFilter;
    if (search) params.search = search;
    axios.get(`${API}/candidate-browser/candidates`, { ...authHeader(), params })
      .then((r) => {
        if (cancelled) return;
        setCandidates(r.data?.data || []); setTotal(r.data?.total || 0); setPages(r.data?.pages || 1);
      })
      .catch((err) => {
        console.error(err);
        if (!cancelled) setCandError(err.response?.data?.message || "Could not load candidates.");
      })
      .finally(() => { if (!cancelled) setCandLoading(false); });
    return () => { cancelled = true; };
  }, [selectedJd, page, statusFilter, search, candReload]);

  useEffect(() => {
    if (!selectedJd) return;
    let cancelled = false;
    axios.get(`${API}/candidate-browser/status-summary`, { ...authHeader(), params:{ jddetails_id:selectedJd._id } })
      .then((r) => { if (!cancelled) setStatusSummary(r.data?.data || {}); })
      .catch(console.error);
    return () => { cancelled = true; };
  }, [selectedJd]);

  const handleSearchChange = (val) => {
    setSearchInput(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setSearch(val); setPage(1); }, 350);
  };

  const clearSearch = () => {
    clearTimeout(searchTimer.current);
    setSearchInput(""); setSearch(""); setPage(1);
  };

  const handleSelectJd = (jd) => {
    clearTimeout(searchTimer.current);
    setSelectedJd(jd); setPage(1); setSearch(""); setSearchInput(""); setStatusFilter(""); setCandidates([]);
  };

  const openDetail = (id) => { setSelectedCandId(id); setDrawerOpen(true); };

  const jdPaneLabel = selectedCompany && !jdsLoading && jds.length > 0
    ? `${selectedCompany.companyName} · ${jds.length} JD${jds.length!==1?"s":""}`
    : "Job Descriptions";

  return (
    <ThemeProvider theme={recruiterTheme}>
    <Box display="flex" height={height} overflow="hidden" bgcolor="#f8fafc" sx={{ fontFamily:FONT_BODY }}>
        <CompanySidebar companies={displayedCompanies} loading={companiesLoading} selected={selectedCompany}
          error={companiesError} onRetry={() => setCompaniesReload((k) => k + 1)}
          onSelect={(c) => { setSelectedComp(c); setSelectedJd(null); }} />

      {/* JD panel */}
      <Box sx={{ width:340, flexShrink:0, borderRight:"1px solid #e2e8f0", bgcolor:"#fff", display:"flex", flexDirection:"column", minWidth:0 }}>
        <PaneHeader label={jdPaneLabel} title={jdPaneLabel} />
        <Box sx={{ flex:1, overflowY:"auto", p:2 }}>
        {!selectedCompany ? (
          <PaneEmpty icon={BusinessIcon} text="Select a company" />
        ) : jdsLoading ? (
          Array.from({length:4}).map((_,i) => <Skeleton key={i} height={72} sx={{ borderRadius:`${RADIUS.sm}px`, mb:1 }} />)
        ) : jdsError ? (
          <PaneError text={jdsError} onRetry={() => setJdsReload((k) => k + 1)} />
        ) : jds.length === 0 ? (
          <PaneEmpty icon={WorkIcon} text="No JDs found" />
        ) : (
          <Box>
            {jds.map((jd) => (
              <JdAccordion key={jd._id} jd={jd}
                  isOpen={openJdId===jd._id} onToggle={(id) => setOpenJdId(id)}
                  selectedJdId={selectedJd?._id} onSelectJd={handleSelectJd} />
            ))}
          </Box>
        )}
        </Box>
      </Box>

      {/* Candidate list */}
      <Box flex={1} overflow="hidden" display="flex" flexDirection="column" minWidth={0}>
        <PaneHeader label="Candidates" />
        {!selectedJd ? (
          <PaneEmpty icon={WorkIcon} text="Select a JD to view candidates" sx={{ flex:1 }} />
        ) : (
          <>
            {/* JD Info Banner */}
<Box sx={{
  px: 3, py: 2, borderBottom: "1px solid #e2e8f0", bgcolor: "#EDE7F6",
  display: "flex", alignItems: "center", columnGap: 3, rowGap: 1.5, flexWrap: "wrap"
}}>
  {/* Title row */}
  <Box flex={1} minWidth={200}>
    <Typography sx={{ fontFamily:FONT_BODY, fontSize:TYPE.cardTitle, fontWeight:700, color:"#0f172a", lineHeight:1.3, ...ELLIPSIS }}>
      {selectedJd.jobRole?.replace(/_/g, " ")}
    </Typography>
    <Typography sx={{ fontFamily:FONT_BODY, fontSize:TYPE.caption, color:"#64748b", fontVariantNumeric:"tabular-nums", ...ELLIPSIS }}>
      {selectedCompany?.companyName} · {selectedJd.jdID} · {total} candidate{total !== 1 ? "s" : ""}
    </Typography>
  </Box>

  {/* JD meta + recruiter */}
  <Box display="flex" columnGap={3} rowGap={1} flexWrap="wrap" alignItems="center">
    {[
      ["Experience", selectedJd.experience ? `${selectedJd.experience} yrs` : "—"],
      ["Language",   selectedJd.programmingLanguage || "—"],
      ["Salary",     selectedJd.salaryRange || "—"],
      ["Positions",  selectedJd.openPositions ?? "—"],
      ["Expiry",     selectedJd.expiration_time
        ? fmtShortDate(selectedJd.expiration_time)
        : "—"],
    ].map(([label, val]) => (
      <Box key={label}>
        <Typography sx={META_LABEL_SX}>
          {label}
        </Typography>
        <Typography sx={META_VALUE_SX}>{val}</Typography>
      </Box>
    ))}

    {/* Recruiter chip */}
    {selectedJd.recruiterName && (
      <Box display="flex" alignItems="center" gap={1}
        sx={{ px: 1.5, py: 0.5, bgcolor: "#fff", border: "1px solid #6750A4", borderRadius: `${RADIUS.sm}px` }}>
        <Avatar sx={{ width: 24, height: 24, fontSize: TYPE.overline, bgcolor: "#8B7DB5" }}>
          {selectedJd.recruiterName[0]}
        </Avatar>
        <Box minWidth={0}>
          <Typography sx={META_LABEL_SX}>
            Recruiter
          </Typography>
          <Typography sx={{ fontFamily:FONT_BODY, fontSize:TYPE.caption, fontWeight:600, color:"#6750A4", whiteSpace:"nowrap" }}>
            {selectedJd.recruiterName}
          </Typography>
        </Box>
      </Box>
    )}

    <Chip
      label={selectedJd.is_active ? "Active" : "Closed"}
      size="small"
      color={selectedJd.is_active ? "success" : "default"}
      sx={PILL_SX}
    />
  </Box>

  {/* Search + filter — same as before, just moved here */}
  <Box display="flex" gap={1.5} alignItems="center" width="100%" flexWrap="wrap">
    <TextField
      size="small"
      placeholder="Search name, email, ID…"
      value={searchInput}
      onChange={(e) => handleSearchChange(e.target.value)}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
          </InputAdornment>
        ),
        endAdornment: searchInput ? (
          <InputAdornment position="end">
            <IconButton size="small" aria-label="Clear search" onClick={clearSearch}>
              <ClearIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </InputAdornment>
        ) : null,
      }}
      sx={{ width: 280, "& .MuiOutlinedInput-root": { height: CONTROL_HEIGHT.lg, borderRadius: `${RADIUS.sm}px`, bgcolor: "#fff", fontFamily: FONT_BODY, fontSize: TYPE.body } }}
    />
    <Select
      size="small"
      value={statusFilter}
      onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
      displayEmpty
      sx={{ minWidth: 200, height: CONTROL_HEIGHT.lg, borderRadius: `${RADIUS.sm}px`, bgcolor: "#fff", fontFamily: FONT_BODY, fontSize: TYPE.body }}
      startAdornment={<FilterListIcon sx={{ fontSize: 18, color: "#94a3b8", mr: 0.5 }} />}
    >
      <MenuItem value=""><em>All statuses</em></MenuItem>
      {ALL_STATUSES.map((s) => (
        <MenuItem key={s} value={s} sx={{ fontSize: TYPE.small }}>
          {humanizeStatus(s)}
        </MenuItem>
      ))}
    </Select>
  </Box>
</Box>

            <Box px={3} pt={2}><StatusBar summary={statusSummary} /></Box>

            <Box flex={1} sx={{ overflowY:"auto" }} px={3} pb={2}>
              {candLoading ? (
                Array.from({length:5}).map((_,i) => <Skeleton key={i} height={90} sx={{ borderRadius:`${RADIUS.md}px`, mb:1 }} />)
              ) : candError ? (
                <PaneError text={candError} onRetry={() => setCandReload((k) => k + 1)} />
              ) : candidates.length === 0 ? (
                <PaneEmpty icon={PersonIcon} text={search || statusFilter ? "No candidates match your filters" : "No candidates for this JD yet"} />
              ) : (
                <Box display="flex" flexDirection="column" gap={1}>
                  {candidates.map((c) => (
                    <CandidateCard key={c._id||c.candidateID} c={c} onClick={() => openDetail(c._id)} />
                  ))}
                </Box>
              )}
            </Box>

            {pages > 1 && (
              <Box display="flex" justifyContent="center" py={1.5} borderTop="1px solid #e2e8f0" bgcolor="#fff">
                <Pagination count={pages} page={page} onChange={(_,v) => setPage(v)} size="small" color="primary" shape="rounded" />
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Detail Drawer */}
      <CandidateDetailDrawer candidateId={selectedCandId} open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </Box>
    </ThemeProvider>
  );
}
